import React, { useState, useCallback, useMemo, type SyntheticEvent } from 'react';
import Button from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
	ModalTransition,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useIssueAttachmentsActions } from '@atlassian/jira-issue-attachments-base/src/services/attachments-service/main.tsx';
import { filmStripView } from '@atlassian/jira-issue-attachments-base/src/ui/types.tsx';
import type { AttachmentId } from '@atlassian/jira-issue-attachments-store/src/common/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import i18n from './messages.tsx';
import type { Props } from './types.tsx';

const useDeletingAttachmentId = () => useState<AttachmentId>();

export const useOnDeleteAttachment = ({ issueKey }: Props) => {
	const [, { deleteAttachment }] = useIssueAttachmentsActions();
	const [deletingAttachmentId, setDeletingAttachmentId] = useDeletingAttachmentId();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onDeleteAttachmentStart = useCallback(
		(attachmentId: string) => {
			setDeletingAttachmentId(attachmentId);
			getUpdateAnalyticsFlowHelper().fireAnalyticsStart('attachment', {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: 'attachment',
					actionTaken: 'remove',
				},
			});
		},
		[setDeletingAttachmentId, createAnalyticsEvent],
	);

	const onDeleteAttachment = useCallback(
		(event: SyntheticEvent<HTMLElement>) => {
			if (deletingAttachmentId !== undefined) {
				event.stopPropagation();
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				});
				fireUIAnalytics(analyticsEvent, 'deleteAttachmentConfirm', {
					view: filmStripView,
				});
				deleteAttachment(issueKey, deletingAttachmentId);
				// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
				setDeletingAttachmentId();
			}
		},
		[
			createAnalyticsEvent,
			deleteAttachment,
			deletingAttachmentId,
			issueKey,
			setDeletingAttachmentId,
		],
	);

	const onDismissModal = useCallback(
		(event: KeyboardOrMouseEvent) => {
			event.stopPropagation();
			// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
			setDeletingAttachmentId();
		},
		[setDeletingAttachmentId],
	);

	const ModelTransition = useMemo(
		() => (
			<ModalTransition>
				{deletingAttachmentId !== undefined && (
					<ShortcutScope>
						<ModalDialog onClose={onDismissModal} width="small">
							<ModalHeader>
								<ModalTitle appearance="danger">
									{formatMessage(i18n.confirmationHeader)}
								</ModalTitle>
							</ModalHeader>
							<ModalBody>{formatMessage(i18n.confirmationContent)}</ModalBody>
							<ModalFooter>
								<Button
									appearance="danger"
									onClick={onDeleteAttachment}
									testId="issue-view-base.content.attachment.filmstrip-view-relay.services.delete-attachments.confirm-button"
									interactionName="auto-generated.issue-attachments-base.ui.with-issue-attachments-delete.confirm-button.button"
								>
									{formatMessage(i18n.confirmationSubmit)}
								</Button>
								<Button
									appearance="subtle"
									onClick={onDismissModal}
									testId="issue-view-base.content.attachment.filmstrip-view-relay.services.delete-attachments.cancel-button"
									interactionName="auto-generated.issue-attachments-base.ui.with-issue-attachments-delete.cancel-button.button1"
								>
									{formatMessage(i18n.confirmationCancel)}
								</Button>
							</ModalFooter>
						</ModalDialog>
					</ShortcutScope>
				)}
			</ModalTransition>
		),
		[deletingAttachmentId, formatMessage, onDismissModal, onDeleteAttachment],
	);

	return useMemo(
		() => ({
			onDeleteAttachment: fg('one-update-event-analytics-attach-file')
				? onDeleteAttachmentStart
				: setDeletingAttachmentId,
			ModelTransition,
		}),
		[ModelTransition, setDeletingAttachmentId, onDeleteAttachmentStart],
	);
};
